import { useCallback } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import { Button } from '../../../../components/buttons'
import { EditorToolbar } from './EditorToolbar'
import { useMentionData } from './mentionUtils'
import { createEditorExtensions } from './editorConfig'
import {
  EditorContainer,
  EditorWrapper,
  MarkupToolbarItems,
  ButtonContainer,
  CancelButton,
} from './EditorStyles'
import { intl } from '../../../../../i18n'

interface RichTextEditorProps {
  placeholder: string
  onSubmit: (content: string) => void
  onCancel?: () => void
  showCancel?: boolean
  submitLabel: string
  projectUuid?: string
}

export const RichTextEditor = ({
  placeholder,
  onSubmit,
  onCancel,
  showCancel = false,
  submitLabel,
  projectUuid,
}: RichTextEditorProps) => {
  const { getMentionData, getProjectMentionData } = useMentionData()

  const editor = useEditor(
    {
      extensions: createEditorExtensions(
        placeholder,
        getMentionData,
        getProjectMentionData,
        projectUuid
      ),
      content: '',
      parseOptions: {
        preserveWhitespace: 'full',
      },
    },
    [projectUuid]
  )

  const handleSubmit = useCallback(() => {
    const html = editor?.getHTML() || ''
    const text = editor?.getText() || ''

    if (text.trim()) {
      onSubmit(html)
      editor?.commands.clearContent()
    }
  }, [editor, onSubmit])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
        const text = editor?.getText() || ''
        if (text.trim()) {
          handleSubmit()
        }
      }
      if (event.key === 'Escape' && showCancel && onCancel) {
        onCancel()
      }
    },
    [editor, handleSubmit, showCancel, onCancel]
  )

  const isEmptyEditor = () => {
    const text = editor?.getText() || ''
    return text.trim() === ''
  }

  if (!editor) {
    return null
  }

  return (
    <EditorContainer>
      <EditorWrapper className="flagxs-markup-root" onKeyDown={handleKeyDown}>
        <EditorContent editor={editor} />
        <MarkupToolbarItems>
          <EditorToolbar editor={editor} />
        </MarkupToolbarItems>
      </EditorWrapper>
      <ButtonContainer>
        <Button onClick={handleSubmit} disabled={isEmptyEditor()}>
          {submitLabel}
        </Button>
        {showCancel && onCancel && (
          <Button onClick={onCancel} colorPattern="monotone" variant="outlined">
            {intl.formatMessage({ id: 'dialog.cancel' })}
          </Button>
        )}
      </ButtonContainer>
    </EditorContainer>
  )
}
